import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  Dropdown,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
  TextInput,
  Menu,
  MenuItem,
} from '@pella/sash-framework';

import { useThrive } from '../../../hooks';
import styles from './SparePartSelector.module.css';

/**
 * SparePartSelector — typeahead combo-box for selecting a spare part on a work order.
 * @param {object} props
 * @param {(part: object | null) => void} props.sparePartOnChange Called with the selected part, or null when the input is cleared.
 * @param {boolean} props.showTitle Whether to render the title/badge block above the input.
 * @param {string} [props.title] Title text shown when showTitle is true.
 * @param {object} [props.workOrder] Work order whose equipNum drives the useSpareParts query.
 * @returns {import('react').ReactElement}
 */
const SparePartSelector = (props) => {
  const { workOrder, showTitle, title, sparePartOnChange: onSparePartChange } = props;
  const { spareParts } = useThrive().useSpareParts({ equipnumber: workOrder?.equipNum });

  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);

  const sortedSparePartsList = useMemo(() => {
    if (!spareParts?.length) return [];
    return [...spareParts].toSorted((a, b) => {
      const dateA = format(new Date(a?.dateUsed), 'yyyy-MM-dd');
      const dateB = format(new Date(b?.dateUsed), 'yyyy-MM-dd');
      return dateB.localeCompare(dateA);
    });
  }, [spareParts]);

  const filtered = useMemo(() => {
    const needle = searchTerm.toLowerCase();
    return sortedSparePartsList.filter((part) =>
      `(${part?.partNum ?? ''}) ${part?.description ?? ''}`.toLowerCase().includes(needle),
    );
  }, [sortedSparePartsList, searchTerm]);

  const handleInputChange = (e) => {
    const next = e.target.value;
    setSearchTerm(next);
    setOpen(true);
    if (next === '') onSparePartChange(null);
  };

  const handleSelect = (part) => {
    setSearchTerm(`(${part?.partNum ?? ''}) ${part?.description ?? ''}`);
    setOpen(false);
    onSparePartChange(part);
  };

  return (
    <div className={styles.container}>
      {showTitle ? (
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h3>{title || 'Select Item'}</h3>
            <span className={styles.badge}>{spareParts?.length ?? 0}</span>
          </div>
        </div>
      ) : null}
      <Dropdown defaultOpen={open} onOpenChange={setOpen}>
        <DropdownTrigger>
          <TextInput
            id='spare-part-combo'
            labelText='Select Part'
            placeholder='Choose a part...'
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={() => setOpen(true)}
            containerClass={styles.inputWrapper}
            autoComplete='off'
            required
            aria-required='true'
          />
        </DropdownTrigger>
        <DropdownContent className={styles.popover} align='start' offset={0}>
          <DropdownItem>
            <Menu unstyled>
              {!filtered.length ? (
                <MenuItem>No matching parts</MenuItem>
              ) : (
                filtered.map((part, idx) => (
                  <MenuItem key={part?.partNum ?? idx} onClick={() => handleSelect(part)}>
                    ({part?.partNum ?? ''}) {part?.description ?? ''}
                  </MenuItem>
                ))
              )}
            </Menu>
          </DropdownItem>
        </DropdownContent>
      </Dropdown>
    </div>
  );
};

export default SparePartSelector;
